import { useState, useRef, useCallback, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { useAuth, SignInModal } from "./auth";
import { BetaGate } from "./beta";
import { FlagsProvider, useFlag } from "./flags";
import { linkForExample } from "./brands";
import AdminPage from "./admin";
import buildInfo from "./build-info.json";

const OCCASIONS = [
  { id: "Everyday", label: "Everyday",  desc: "Casual day-to-day looks" },
  { id: "Work",     label: "Work",      desc: "Office and professional settings" },
  { id: "Evening",  label: "Evening",   desc: "Dinner, dates, and evening events" },
  { id: "Formal",   label: "Formal",    desc: "Black tie, galas, and formal events" },
  { id: "Sport",    label: "Sport",     desc: "Athletic and activewear" },
  { id: "Night Out",label: "Night Out", desc: "Bars, clubs, and late night" },
];

const TONES = [
  { id: "stylist",   label: "Stylist",   desc: "Personal stylist — specific piece and brand recommendations" },
  { id: "honest",    label: "Honest",    desc: "Straight talk from a sharp fashion editor" },
  { id: "roast",     label: "Roast",     desc: "Full comedy roast energy, real advice underneath" },
  { id: "snarky",    label: "Snarky",    desc: "Witty and cutting — thinks it's funnier than you" },
  { id: "kind",      label: "Kind",      desc: "Warm and encouraging, like a stylish best friend" },
  { id: "editorial", label: "Editorial", desc: "Pretentious high-fashion magazine speak" },
];

// When the limit_vibes flag is on, only these tone ids are offered.
const LIMITED_TONE_IDS = ["stylist", "honest", "roast"];

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Montserrat:wght@300;400&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #f8f7f4; }
  .app { min-height: 100vh; background: #f8f7f4; color: #000; font-family: 'Montserrat', sans-serif; font-weight: 300; }
  .masthead { border-bottom: 1px solid #000; padding: 28px 48px; display: flex; align-items: baseline; justify-content: space-between; }
  .masthead-link { text-decoration: none; color: inherit; }
  .masthead-link:hover .masthead-title { opacity: 0.6; }
  .masthead-title { font-family: 'Cormorant Garamond', serif; font-size: 1.1rem; font-weight: 400; letter-spacing: 0.25em; text-transform: uppercase; transition: opacity 0.2s; }
  .masthead-sub { display: block; font-size: 0.55rem; letter-spacing: 0.28em; text-transform: uppercase; color: #444; margin-top: 4px; }
  .main { max-width: 960px; margin: 0 auto; padding: 64px 48px 120px; }
  .hero { margin-bottom: 72px; }
  .hero-label { font-size: 0.6rem; letter-spacing: 0.35em; text-transform: uppercase; color: #444; margin-bottom: 20px; }
  .hero-headline { font-family: 'Cormorant Garamond', serif; font-size: clamp(3.5rem, 8vw, 6.5rem); font-weight: 300; line-height: 0.95; letter-spacing: -0.01em; color: #000; }
  .hero-headline em { font-style: italic; }
  .upload-zone { border: 1px solid #444; aspect-ratio: 3/4; max-height: 520px; display: flex; flex-direction: column; align-items: center; justify-content: center; cursor: pointer; transition: border-color 0.2s; background: #f0efe9; position: relative; overflow: hidden; }
  .upload-zone:hover { border-color: #000; }
  .upload-zone.has-image { border-color: #000; cursor: default; }
  .upload-zone.drag { border-color: #000; background: #e8e8e2; }
  .upload-icon { font-size: 1.5rem; margin-bottom: 16px; opacity: 0.4; }
  .upload-text { font-size: 0.65rem; letter-spacing: 0.2em; text-transform: uppercase; color: #444; }
  .preview-img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: start; }
  .right-panel { padding-top: 8px; }
  .section-label { font-size: 0.6rem; letter-spacing: 0.3em; text-transform: uppercase; color: #444; margin-bottom: 16px; display: block; }
  .occasions { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
  .occ-btn { font-family: 'Montserrat', sans-serif; font-size: 0.6rem; letter-spacing: 0.2em; text-transform: uppercase; padding: 10px 18px; border: 1px solid #444; background: transparent; cursor: pointer; color: #000; transition: all 0.15s; font-weight: 300; }
  .occ-btn.active { border-color: #000; background: #000; color: #f8f7f4; }
  .tones { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
  .tone-btn { font-family: 'Montserrat', sans-serif; font-size: 0.6rem; letter-spacing: 0.2em; text-transform: uppercase; padding: 10px 18px; border: 1px solid #444; background: transparent; cursor: pointer; color: #000; transition: all 0.15s; font-weight: 300; }
  .tone-btn.active { border-color: #000; background: #000; color: #f8f7f4; }
  .selection-desc { font-size: 0.62rem; color: #444; letter-spacing: 0.05em; min-height: 1.4em; margin-bottom: 28px; padding-left: 2px; font-style: italic; }
  .example-prompt { margin-top: 56px; text-align: center; }
  .example-link { font-family: 'Cormorant Garamond', serif; font-size: 1rem; font-style: italic; color: #000; text-decoration: none; border-bottom: 1px solid #444; padding-bottom: 2px; transition: border-color 0.15s; }
  .example-link:hover { border-bottom-color: #000; }
  .explainer { margin-top: 120px; padding-top: 80px; border-top: 1px solid #444; }
  .explainer-block { margin-bottom: 72px; }
  .explainer-block:last-child { margin-bottom: 0; }
  .explainer-block .section-label { margin-bottom: 32px; }
  .steps { display: grid; grid-template-columns: 1fr; gap: 32px; }
  .step { display: grid; grid-template-columns: 60px 1fr; gap: 16px; align-items: baseline; padding-bottom: 28px; border-bottom: 1px solid #aaa; }
  .step:last-child { border-bottom: none; }
  .step-num { font-family: 'Cormorant Garamond', serif; font-size: 1.6rem; font-style: italic; color: #444; }
  .step-body { display: flex; flex-direction: column; gap: 8px; }
  .step-title { font-family: 'Cormorant Garamond', serif; font-size: 1.35rem; font-weight: 400; color: #000; letter-spacing: -0.005em; line-height: 1.2; }
  .step-text { font-size: 0.85rem; line-height: 1.7; color: #000; }
  .explainer-prose { font-size: 0.9rem; line-height: 1.75; color: #000; margin-bottom: 16px; max-width: 640px; }
  .explainer-prose:last-child { margin-bottom: 0; }
  .turnstile-wrap { margin-bottom: 16px; display: flex; justify-content: center; }
  .cta { width: 100%; padding: 20px; border: none; background: #000; color: #f8f7f4; font-family: 'Montserrat', sans-serif; font-size: 0.65rem; font-weight: 400; letter-spacing: 0.3em; text-transform: uppercase; cursor: pointer; transition: opacity 0.2s; margin-bottom: 16px; }
  .cta:disabled { opacity: 0.3; cursor: not-allowed; }
  .cta:hover:not(:disabled) { opacity: 0.8; }
  .remove-btn { background: none; border: none; font-family: 'Montserrat', sans-serif; font-size: 0.6rem; letter-spacing: 0.2em; text-transform: uppercase; color: #444; cursor: pointer; padding: 8px 0; }
  .remove-btn:hover { color: #000; }
  .loading { padding: 40px 0 0; }
  .loading-text { font-size: 0.6rem; letter-spacing: 0.35em; text-transform: uppercase; color: #444; margin-bottom: 16px; min-height: 1.2em; transition: opacity 0.3s; }
  .progress-bar { width: 100%; max-width: 320px; height: 2px; background: #aaa; overflow: hidden; }
  .progress-bar-fill { height: 100%; background: #000; transition: width 0.3s ease-out; }
  .error { font-size: 0.75rem; color: #c0392b; padding: 20px 24px; border: 1px solid #c0392b; margin-top: 24px; line-height: 1.6; overflow-wrap: break-word; }
  .result { animation: rise 0.6s ease forwards; opacity: 0; transform: translateY(20px); }
  @keyframes rise { to { opacity:1; transform:translateY(0) } }
  .verdict-row { display: flex; align-items: flex-end; justify-content: space-between; padding-bottom: 24px; border-bottom: 1px solid #000; margin-bottom: 40px; }
  .verdict-word { font-family: 'Cormorant Garamond', serif; font-size: clamp(3rem, 7vw, 5.5rem); font-weight: 300; line-height: 1; letter-spacing: -0.02em; }
  .verdict-word.works { color: #000; }
  .verdict-word.almost { color: #8a7a5a; }
  .verdict-word.rework { color: #c0392b; }
  .score-block { text-align: right; }
  .score-num { font-family: 'Cormorant Garamond', serif; font-size: 3rem; font-weight: 300; line-height: 1; }
  .score-denom { font-size: 0.65rem; letter-spacing: 0.2em; color: #444; }
  .tone-badge { font-size: 0.55rem; letter-spacing: 0.2em; text-transform: uppercase; padding: 4px 10px; border: 1px solid #444; color: #444; display: inline-block; margin-bottom: 24px; }
  .summary { font-family: 'Cormorant Garamond', serif; font-size: 1.25rem; font-weight: 300; line-height: 1.75; color: #000; margin-bottom: 48px; font-style: italic; }
  .feedback-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0; border-top: 1px solid #444; margin-bottom: 40px; }
  .feedback-col { padding: 32px 0; }
  .feedback-col:first-child { padding-right: 32px; border-right: 1px solid #444; }
  .feedback-col:last-child { padding-left: 32px; }
  .feedback-item { font-size: 0.78rem; line-height: 1.8; color: #000; margin-bottom: 12px; padding-left: 16px; position: relative; }
  .feedback-item::before { content: '—'; position: absolute; left: 0; color: #444; }
  .ideas-section { border-top: 1px solid #444; padding-top: 32px; }
  .idea-item { display: flex; gap: 24px; padding: 16px 0; border-bottom: 1px solid #aaa; align-items: baseline; }
  .idea-num { font-family: 'Cormorant Garamond', serif; font-size: 0.85rem; color: #666; flex-shrink: 0; width: 24px; }
  .idea-text { font-size: 0.78rem; line-height: 1.7; color: #000; }
  .rec-item { padding: 24px 0; border-bottom: 1px solid #aaa; }
  .rec-item:last-child { border-bottom: none; }
  .rec-header { display: flex; align-items: baseline; gap: 16px; margin-bottom: 10px; }
  .rec-num { font-family: 'Cormorant Garamond', serif; font-size: 0.85rem; color: #666; flex-shrink: 0; width: 24px; }
  .rec-piece { font-family: 'Cormorant Garamond', serif; font-size: 1.1rem; font-style: italic; flex: 1; color: #000; }
  .rec-tier { font-size: 0.65rem; letter-spacing: 0.15em; color: #8a6e3e; font-weight: 400; }
  .rec-why { font-size: 0.78rem; line-height: 1.7; color: #000; padding-left: 40px; margin-bottom: 8px; }
  .rec-examples { font-size: 0.7rem; letter-spacing: 0.03em; color: #444; padding-left: 40px; margin-bottom: 8px; font-style: italic; }
  .example-shop-link { color: #444; text-decoration: none; border-bottom: 1px solid #aaa; transition: color 0.15s, border-color 0.15s; }
  .example-shop-link:hover { color: #000; border-bottom-color: #000; }
  .rec-how { font-size: 0.72rem; line-height: 1.7; color: #000; padding-left: 40px; }
  .rec-how::before { content: 'How: '; color: #8a6e3e; font-weight: 400; text-transform: uppercase; font-size: 0.6rem; letter-spacing: 0.15em; }
  .product-card-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-top: 20px; padding-left: 40px; }
  .product-card { display: flex; flex-direction: column; border: 1px solid #444; background: #fff; text-decoration: none; color: #000; transition: border-color 0.15s; overflow: hidden; }
  .product-card:hover { border-color: #000; }
  .product-card-img-wrap { width: 100%; aspect-ratio: 4/5; background: #f0efe9; overflow: hidden; }
  .product-card-img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .product-card-body { padding: 10px 12px 12px; display: flex; flex-direction: column; gap: 4px; }
  .product-card-brand { font-size: 0.55rem; letter-spacing: 0.18em; text-transform: uppercase; color: #444; font-weight: 400; }
  .product-card-title { font-size: 0.7rem; line-height: 1.35; color: #000; min-height: 1.9em; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  .product-card-meta { display: flex; justify-content: space-between; align-items: baseline; margin-top: 4px; }
  .product-card-price { font-family: 'Cormorant Garamond', serif; font-size: 0.95rem; color: #000; }
  .product-card-retailer { font-size: 0.5rem; letter-spacing: 0.12em; text-transform: uppercase; color: #666; }
  .affiliate-disclosure { margin-top: 32px; font-size: 0.62rem; color: #444; text-align: center; font-style: italic; line-height: 1.6; }
  .signature { margin-top: 40px; padding-top: 32px; border-top: 1px solid #444; }
  .signature-text { font-family: 'Cormorant Garamond', serif; font-size: 1.2rem; font-style: italic; line-height: 1.6; color: #000; margin-top: 12px; }
  .reset-row { margin-top: 56px; display: flex; justify-content: center; gap: 40px; }
  .reset-btn { background: none; border: none; font-family: 'Montserrat', sans-serif; font-size: 0.6rem; letter-spacing: 0.25em; text-transform: uppercase; color: #444; cursor: pointer; }
  .reset-btn:hover { color: #000; }
  .share-btn { width: 100%; padding: 20px; border: 1px solid #000; background: transparent; color: #000; font-family: 'Montserrat', sans-serif; font-size: 0.65rem; font-weight: 400; letter-spacing: 0.3em; text-transform: uppercase; cursor: pointer; transition: all 0.2s; margin-bottom: 16px; }
  .share-btn:hover { background: #000; color: #f8f7f4; }
  .auth-chip { font-family: 'Montserrat', sans-serif; font-size: 0.6rem; letter-spacing: 0.2em; text-transform: uppercase; background: none; border: 1px solid #444; padding: 7px 14px; color: #000; cursor: pointer; font-weight: 300; transition: all 0.15s; }
  .auth-chip:hover { border-color: #000; color: #000; }
  .auth-email { font-size: 0.62rem; color: #444; letter-spacing: 0.04em; max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .masthead-auth { display: flex; align-items: center; gap: 14px; }
  .modal-backdrop { position: fixed; inset: 0; background: rgba(26,26,24,0.55); display: flex; align-items: center; justify-content: center; z-index: 100; animation: fadeIn 0.2s ease; }
  @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
  .modal { background: #f8f7f4; padding: 48px 40px; max-width: 420px; width: 90%; position: relative; border: 1px solid #000; animation: rise 0.3s ease forwards; }
  .modal-close { position: absolute; top: 14px; right: 18px; background: none; border: none; font-size: 1.6rem; line-height: 1; cursor: pointer; color: #444; font-family: inherit; }
  .modal-close:hover { color: #000; }
  .modal-title { font-family: 'Cormorant Garamond', serif; font-size: 2.2rem; font-weight: 300; margin-bottom: 6px; letter-spacing: -0.01em; }
  .modal-sub { font-size: 0.75rem; color: #444; margin-bottom: 32px; line-height: 1.6; }
  .auth-btn { display: block; width: 100%; padding: 16px; font-family: 'Montserrat', sans-serif; font-weight: 400; font-size: 0.65rem; letter-spacing: 0.25em; text-transform: uppercase; border: 1px solid #000; background: transparent; color: #000; cursor: pointer; margin-bottom: 12px; transition: all 0.15s; }
  .auth-btn:hover:not(:disabled) { background: #000; color: #f8f7f4; }
  .auth-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .auth-btn-google { background: #000; color: #f8f7f4; }
  .auth-btn-google:hover:not(:disabled) { background: #000; }
  .email-input { display: block; width: 100%; padding: 14px 16px; font-family: inherit; font-size: 0.9rem; border: 1px solid #444; background: #fff; margin-bottom: 12px; border-radius: 0; }
  .email-input:focus { outline: none; border-color: #000; }
  .auth-back { display: block; width: 100%; background: none; border: none; font-family: 'Montserrat', sans-serif; font-size: 0.58rem; letter-spacing: 0.2em; text-transform: uppercase; color: #444; cursor: pointer; padding: 12px 0; margin-top: 4px; }
  .auth-back:hover { color: #000; }
  .email-sent { font-size: 0.95rem; line-height: 1.7; color: #000; padding: 22px 24px; background: #aaa; font-family: 'Cormorant Garamond', serif; font-style: italic; }
  .modal-error { margin-top: 16px; padding: 12px 16px; font-size: 0.72rem; color: #c0392b; border: 1px solid #c0392b; }
  .version-tag { position: fixed; bottom: 10px; right: 14px; font-family: 'Montserrat', monospace; font-size: 0.55rem; letter-spacing: 0.08em; color: #444; text-decoration: none; opacity: 0.5; transition: opacity 0.15s, color 0.15s; z-index: 50; user-select: none; }
  .version-tag:hover { opacity: 1; color: #000; }
  .gate { min-height: 100vh; background: #f8f7f4; display: flex; align-items: center; justify-content: center; padding: 32px; }
  .gate-inner { max-width: 440px; width: 100%; text-align: center; }
  .gate-title { font-family: 'Cormorant Garamond', serif; font-size: clamp(3rem, 8vw, 5rem); font-weight: 300; letter-spacing: -0.01em; color: #000; margin-bottom: 12px; line-height: 1; }
  .gate-sub { font-size: 0.65rem; color: #444; letter-spacing: 0.25em; text-transform: uppercase; font-weight: 300; margin-bottom: 40px; }
  .gate-input { display: block; width: 100%; padding: 18px 16px; font-family: 'Montserrat', monospace; font-size: 0.85rem; letter-spacing: 0.3em; text-align: center; text-transform: uppercase; border: 1px solid #444; background: #fff; margin-bottom: 12px; border-radius: 0; outline: none; color: #000; }
  .gate-input:focus { border-color: #000; }
  .gate-btn { display: block; width: 100%; padding: 18px; font-family: 'Montserrat', sans-serif; font-weight: 400; font-size: 0.65rem; letter-spacing: 0.3em; text-transform: uppercase; border: 1px solid #000; background: #000; color: #f8f7f4; cursor: pointer; transition: opacity 0.15s; }
  .gate-btn:hover:not(:disabled) { opacity: 0.75; }
  .gate-btn:disabled { opacity: 0.3; cursor: not-allowed; }
  .gate-error { margin-top: 16px; padding: 12px 16px; font-size: 0.72rem; color: #c0392b; border: 1px solid #c0392b; }
  .admin { max-width: 1200px; margin: 0 auto; padding: 48px 40px; font-family: 'Montserrat', sans-serif; color: #000; }
  .admin-header { display: flex; align-items: flex-start; justify-content: space-between; border-bottom: 1px solid #000; padding-bottom: 24px; margin-bottom: 32px; }
  .admin-title { font-family: 'Cormorant Garamond', serif; font-size: 2.8rem; font-weight: 300; letter-spacing: -0.01em; line-height: 1; }
  .admin-sub { font-size: 0.7rem; color: #444; letter-spacing: 0.1em; text-transform: uppercase; margin-top: 8px; }
  .admin-back { font-size: 0.6rem; letter-spacing: 0.25em; text-transform: uppercase; color: #444; text-decoration: none; padding-top: 8px; }
  .admin-back:hover { color: #000; }
  .admin-gate { max-width: 440px; margin: 120px auto; text-align: center; padding: 0 24px; font-family: 'Montserrat', sans-serif; }
  .admin-gate .admin-title { margin-bottom: 12px; }
  .admin-gate .admin-sub { margin-bottom: 28px; }
  .admin-loading { text-align: center; padding: 80px 0; color: #444; font-size: 0.65rem; letter-spacing: 0.3em; text-transform: uppercase; }
  .admin-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 16px; margin-bottom: 48px; }
  .stat-card { border: 1px solid #444; padding: 24px 20px; background: #f0efe9; }
  .stat-value { font-family: 'Cormorant Garamond', serif; font-size: 2rem; font-weight: 300; line-height: 1.1; }
  .stat-label { font-size: 0.6rem; color: #444; letter-spacing: 0.2em; text-transform: uppercase; margin-top: 8px; }
  .stat-hint { font-size: 0.55rem; color: #666; letter-spacing: 0.05em; margin-top: 6px; font-style: italic; }
  .admin-stats-prominent { grid-template-columns: 2fr 1fr; gap: 16px; margin-bottom: 32px; }
  .stat-card-emphasis { background: #000; color: #f8f7f4; border-color: #000; }
  .stat-card-emphasis .stat-value { font-size: 3rem; color: #f8f7f4; }
  .stat-card-emphasis .stat-label { color: rgba(248,247,244,0.7); }
  .stat-card-emphasis .stat-hint { color: rgba(248,247,244,0.45); }
  .stat-card-link { display: flex; flex-direction: column; justify-content: space-between; text-decoration: none; color: inherit; transition: border-color 0.15s; }
  .stat-card-link:hover { border-color: #000; }
  .stat-value-link { font-family: 'Cormorant Garamond', serif; font-size: 1.4rem; font-weight: 400; font-style: italic; color: #000; }
  .admin-section { margin-bottom: 48px; }
  .admin-section-title { font-family: 'Cormorant Garamond', serif; font-size: 1.3rem; font-weight: 400; margin-bottom: 16px; }
  .admin-table { width: 100%; border-collapse: collapse; font-size: 0.75rem; }
  .admin-table th { text-align: left; padding: 10px 12px; font-size: 0.55rem; letter-spacing: 0.2em; text-transform: uppercase; color: #444; font-weight: 400; border-bottom: 1px solid #000; }
  .admin-table td { padding: 10px 12px; border-bottom: 1px solid #aaa; vertical-align: top; }
  .admin-table tbody tr:hover { background: #f0efe9; }
  .admin-table a { color: #000; text-decoration: underline; }
  .admin-table code { font-family: 'Montserrat', monospace; font-size: 0.75rem; background: #f0efe9; padding: 2px 6px; border-radius: 2px; }
  .admin-error { margin-top: 12px; padding: 10px 14px; font-size: 0.7rem; color: #c0392b; border: 1px solid #c0392b; }
  .flag-toggle { font-family: 'Montserrat', sans-serif; font-size: 0.55rem; letter-spacing: 0.22em; text-transform: uppercase; padding: 5px 14px; border: 1px solid #c0392b; background: transparent; color: #c0392b; cursor: pointer; font-weight: 400; transition: opacity 0.15s; min-width: 56px; }
  .flag-toggle.is-on { border-color: #3e6e5a; color: #3e6e5a; }
  .flag-toggle:hover:not(:disabled) { opacity: 0.7; }
  .flag-toggle:disabled { opacity: 0.4; cursor: not-allowed; }
  .flag-add-row { display: flex; gap: 8px; margin-top: 16px; align-items: stretch; }
  .flag-add-input { padding: 8px 12px; font-family: 'Montserrat', sans-serif; font-size: 0.75rem; border: 1px solid #444; background: #fff; border-radius: 0; outline: none; min-width: 0; }
  .flag-add-input:focus { border-color: #000; }
  .flag-add-desc { flex: 1; }
  .flag-add-btn { padding: 8px 18px; font-family: 'Montserrat', sans-serif; font-size: 0.6rem; letter-spacing: 0.2em; text-transform: uppercase; border: 1px solid #000; background: #000; color: #f8f7f4; cursor: pointer; transition: opacity 0.15s; }
  .flag-add-btn:hover:not(:disabled) { opacity: 0.8; }
  .flag-add-btn:disabled { opacity: 0.3; cursor: not-allowed; }
  .anchor-filters { display: flex; gap: 12px; align-items: center; margin-bottom: 12px; }
  .anchor-filter { padding: 6px 10px; font-family: 'Montserrat', sans-serif; font-size: 0.7rem; border: 1px solid #444; background: #fff; border-radius: 0; outline: none; }
  .anchor-filter-count { font-size: 0.62rem; color: #444; letter-spacing: 0.08em; text-transform: uppercase; }
  .anchor-thumb { width: 40px; height: 50px; object-fit: cover; display: block; border: 1px solid #aaa; background: #f0efe9; }
  .anchor-thumb-lg { width: 80px; height: 100px; object-fit: cover; display: block; border: 1px solid #444; background: #f0efe9; }
  .anchor-action-btn { padding: 4px 10px; font-family: 'Montserrat', sans-serif; font-size: 0.55rem; letter-spacing: 0.18em; text-transform: uppercase; border: 1px solid #444; background: transparent; color: #000; cursor: pointer; margin-right: 6px; }
  .anchor-action-btn:hover:not(:disabled) { border-color: #000; }
  .anchor-action-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .anchor-action-danger { border-color: #c0392b; color: #c0392b; }
  .anchor-action-danger:hover:not(:disabled) { background: #c0392b; color: #f8f7f4; }
  .anchor-form { margin-top: 24px; padding: 20px; border: 1px solid #444; background: #f0efe9; }
  .anchor-form-title { font-family: 'Cormorant Garamond', serif; font-size: 1.1rem; font-weight: 400; margin-bottom: 16px; }
  .anchor-form-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px 16px; }
  .anchor-form-grid label { display: flex; flex-direction: column; font-size: 0.6rem; letter-spacing: 0.12em; text-transform: uppercase; color: #444; gap: 4px; }
  .anchor-form-grid input, .anchor-form-grid select { padding: 8px 10px; font-family: 'Montserrat', sans-serif; font-size: 0.78rem; border: 1px solid #444; background: #fff; border-radius: 0; outline: none; color: #000; text-transform: none; letter-spacing: 0; }
  .anchor-form-grid input:focus, .anchor-form-grid select:focus { border-color: #000; }
  .anchor-preview { margin-top: 16px; }
  .anchor-form-actions { margin-top: 16px; display: flex; gap: 12px; align-items: center; }
  .meta-row { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; margin-bottom: 24px; }
  .meta-row .tone-badge { margin-bottom: 0; }
  .visibility-chip { font-family: 'Montserrat', sans-serif; font-size: 0.55rem; letter-spacing: 0.22em; text-transform: uppercase; padding: 4px 11px; border: 1px solid; background: transparent; cursor: pointer; font-weight: 400; transition: opacity 0.15s; }
  .visibility-chip.is-public { border-color: #3e6e5a; color: #3e6e5a; }
  .visibility-chip.is-private { border-color: #c0392b; color: #c0392b; }
  .visibility-chip:hover:not(:disabled) { opacity: 0.7; }
  .visibility-chip:disabled { opacity: 0.4; cursor: not-allowed; }
  @media (max-width: 640px) {
    .masthead { padding: 20px 24px; }
    .main { padding: 32px 24px 80px; }
    .hero { margin-bottom: 36px; }
    .hero-headline { font-size: clamp(2.5rem, 12vw, 4rem); }
    .grid { grid-template-columns: 1fr; gap: 32px; }
    .upload-zone { aspect-ratio: 4/3; max-height: 280px; }
    .feedback-grid { grid-template-columns: 1fr; }
    .feedback-col:first-child { border-right: none; border-bottom: 1px solid #444; padding-right: 0; padding-bottom: 24px; }
    .feedback-col:last-child { padding-left: 0; }
    .reset-row { flex-direction: column; align-items: center; gap: 20px; }
    .version-tag { display: none; }
    .product-card-row { grid-template-columns: 1fr; padding-left: 16px; }
    .explainer { margin-top: 80px; padding-top: 56px; }
    .explainer-block { margin-bottom: 56px; }
    .step { grid-template-columns: 40px 1fr; gap: 12px; }
  }
`;

// ─── Scroll to top on route change ───────────────────────────────────────────
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// ─── Home page ────────────────────────────────────────────────────────────────
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || null;

function HomePage({ image, imageFile, occasion, setOccasion, tone, setTone, loading, error, drag, setDrag, handleFile, resetAll, analyze, fileRef }) {
  const occDesc  = OCCASIONS.find(o => o.id === occasion)?.desc || "";
  const toneDesc = TONES.find(t => t.id === tone)?.desc || "";

  const limitVibes = useFlag("limit_vibes");
  const turnstileEnabled = useFlag("turnstile_enabled");
  const visibleTones = limitVibes ? TONES.filter(t => LIMITED_TONE_IDS.includes(t.id)) : TONES;

  // If the active tone gets hidden by flipping the flag on, fall back to honest
  // (which is always in the allowed list) so the user isn't stuck on a now-invisible choice.
  useEffect(() => {
    if (limitVibes && !LIMITED_TONE_IDS.includes(tone)) setTone("stylist");
  }, [limitVibes, tone, setTone]);

  // Cloudflare Turnstile: render the bot-check widget only once an image is
  // uploaded (the moment a verdict request becomes possible) AND the
  // turnstile_enabled flag is on. Token is captured via callback and
  // passed through to the analyze submission.
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);
  const turnstileActive = turnstileEnabled && !!TURNSTILE_SITE_KEY;
  useEffect(() => {
    if (!image || !turnstileActive) return;
    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      if (!window.turnstile || !turnstileRef.current) {
        setTimeout(tryRender, 100);
        return;
      }
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setTurnstileToken(token),
        "error-callback": () => setTurnstileToken(null),
        "expired-callback": () => setTurnstileToken(null),
        theme: "light",
      });
    };
    tryRender();
    return () => {
      cancelled = true;
      if (window.turnstile && turnstileWidgetId.current) {
        try { window.turnstile.remove(turnstileWidgetId.current); } catch {}
      }
      turnstileWidgetId.current = null;
      setTurnstileToken(null);
    };
  }, [image, turnstileActive]);

  return (
    <>
      <div className="hero">
        <h1 className="hero-headline">Does it<br /><em>work?</em></h1>
      </div>

      <div className="grid">
        <div
          className={`upload-zone${image ? " has-image" : ""}${drag ? " drag" : ""}`}
          onClick={() => !image && fileRef.current.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
        >
          {image
            ? <img src={image} className="preview-img" alt="Your outfit" />
            : <><div className="upload-icon">+</div><div className="upload-text">Click or drop a photo</div></>
          }
          <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => handleFile(e.target.files[0])} />
        </div>

        <div className="right-panel">
          <span className="section-label">Occasion</span>
          <div className="occasions">
            {OCCASIONS.map(o => (
              <button key={o.id} className={`occ-btn${occasion === o.id ? " active" : ""}`} onClick={() => setOccasion(o.id)}>{o.label}</button>
            ))}
          </div>
          <div className="selection-desc">{occDesc}</div>

          <span className="section-label">Vibe</span>
          <div className="tones">
            {visibleTones.map(t => (
              <button key={t.id} className={`tone-btn${tone === t.id ? ` active ${t.id}` : ""}`} onClick={() => setTone(t.id)}>{t.label}</button>
            ))}
          </div>
          <div className="selection-desc">{toneDesc}</div>

          {image && turnstileActive && (
            <div ref={turnstileRef} className="turnstile-wrap" />
          )}

          {image && (
            <>
              <button
                className="cta"
                onClick={() => analyze(turnstileToken)}
                disabled={loading || (turnstileActive && !turnstileToken)}
              >
                {loading
                  ? "Reading the room..."
                  : (turnstileActive && !turnstileToken)
                    ? "Verifying…"
                    : "Get the verdict"}
              </button>
              <button className="remove-btn" onClick={resetAll}>Remove photo</button>
            </>
          )}

          {loading && <LoadingProgress tone={tone} />}

          {error && <div className="error">{error}</div>}
        </div>
      </div>

      <div className="example-prompt">
        <Link to="/result/6b3bae98-3a1b-4f2b-a10a-db4f95137fce" className="example-link">
          See an example verdict →
        </Link>
      </div>

      <section className="explainer">
        <div className="explainer-block">
          <span className="section-label">How it works</span>
          <div className="steps">
            <div className="step">
              <span className="step-num">01</span>
              <div className="step-body">
                <h3 className="step-title">Upload an outfit photo</h3>
                <p className="step-text">A quick mirror selfie of yourself (or another adult) works. So does a flat lay or a try-on. We resize and re-orient before sending it on, so iPhone rotation quirks don't matter.</p>
              </div>
            </div>
            <div className="step">
              <span className="step-num">02</span>
              <div className="step-body">
                <h3 className="step-title">Pick the occasion and the vibe</h3>
                <p className="step-text">Tell us where you're headed and what kind of feedback you want. Honest is a direct take. Roast is comedy with real advice underneath. Stylist goes deepest — specific pieces, real brands, three price tiers.</p>
              </div>
            </div>
            <div className="step">
              <span className="step-num">03</span>
              <div className="step-body">
                <h3 className="step-title">Get a verdict you can act on</h3>
                <p className="step-text">A written critique tied to the actual garments in your photo, a one-to-ten score, and concrete swaps for what isn't working. Stylist results include shoppable picks at each price tier.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="explainer-block">
          <span className="section-label">Why we built this</span>
          <p className="explainer-prose">
            Most outfit feedback comes from people too polite to be useful. Generic style guides are too vague to act on, and asking strangers online is a lottery. Judge the Fit is the in-between: an honest second opinion, available the moment before you walk out the door, that names the actual pieces in your photo instead of trading in clichés.
          </p>
          <p className="explainer-prose">
            The AI is trained to be specific. It points at the navy plaid flannel — not "the top." It tells you the cream henley would create the contrast the layering needs — not that you should "elevate" the look. And when you pick the Stylist vibe, the recommendations come with named pieces at real price tiers, so you can do something about them.
          </p>
        </div>

        <div className="explainer-block">
          <span className="section-label">What it costs</span>
          <p className="explainer-prose">
            Free to try. We use Claude's vision model to do the analysis, which costs us a few cents per outfit. Some recommendations include affiliate links to products we think actually match — if you buy through one of them, we earn a small commission at no extra cost to you. It's the only way the site makes money.
          </p>
        </div>
      </section>
    </>
  );
}

// Loading indicator that cycles through tone-aware stage messages and shows
// a progress bar that asymptotes toward 95% via exp easing. The bar never
// reaches 100% on its own — only the real response completing unmounts this
// and renders the result. Stage durations were picked to match observed
// p50 latencies (Sonnet vision ≈ 7-10s, stylist tone runs longer).
function LoadingProgress({ tone }) {
  const isStylist = tone === "stylist";
  const stages = isStylist
    ? [
        { at: 0,  text: "Reading your photo" },
        { at: 2,  text: "Studying silhouette and color" },
        { at: 4,  text: "Considering the fit" },
        { at: 7,  text: "Finding pieces that work" },
        { at: 11, text: "Writing your recommendations" },
      ]
    : [
        { at: 0, text: "Reading your photo" },
        { at: 2, text: "Looking at color and shape" },
        { at: 5, text: "Checking the fit" },
        { at: 8, text: "Writing your verdict" },
      ];
  const targetSeconds = isStylist ? 11 : 8;

  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => setElapsed((Date.now() - start) / 1000), 100);
    return () => clearInterval(id);
  }, []);

  const stage = stages.reduce((acc, s) => elapsed >= s.at ? s : acc, stages[0]);
  // 1 - exp(-t / tau): smooth ease toward 1. Pick tau so bar reaches ~85%
  // at the expected completion time, then capped at 95% so it never hits 100
  // before the real response lands.
  const tau = targetSeconds / 1.9;
  const pct = Math.min(95, 100 * (1 - Math.exp(-elapsed / tau)));

  return (
    <div className="loading">
      <div className="loading-text">{stage.text}</div>
      <div className="progress-bar"><div className="progress-bar-fill" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

// Safely construct an Authorization header (Safari rejects malformed values).
function buildAuthHeaders(token) {
  const clean = typeof token === "string" ? token.trim() : "";
  const isJwt = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(clean);
  return isJwt ? { Authorization: `Bearer ${clean}` } : {};
}

// ─── Result page ──────────────────────────────────────────────────────────────
// Two entry paths:
//   1) Just-analyzed — AppInner navigates here with full state
//   2) Direct URL visit — we fetch by :id from /api/analyses/:id
function ResultPage({ resetAll, resetOptions }) {
  const { id: paramId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useAuth();

  const initial = location.state || {};
  const [feedback, setFeedback]           = useState(initial.feedback || null);
  const [image, setImage]                 = useState(initial.image || null);
  const [usedTone, setUsedTone]           = useState(initial.usedTone || "honest");
  const [occasion, setOccasion]           = useState(initial.occasion || "Everyday");
  const [isOwner, setIsOwner]             = useState(initial.isOwner || false);
  const [isPublic, setIsPublic]           = useState(initial.isPublic || false);
  const [loading, setLoading]             = useState(!initial.feedback);
  const [error, setError]                 = useState(null);
  const [copied, setCopied]               = useState(false);
  const [toggling, setToggling]           = useState(false);

  // Fetch when we landed via a share URL (no navigation state).
  useEffect(() => {
    if (initial.feedback || !paramId) return;
    let cancelled = false;
    setLoading(true);
    fetch(`/api/analyses/${paramId}`, { headers: buildAuthHeaders(token) })
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        if (data.error) { setError(data.error); return; }
        setFeedback(data);
        setImage(data.image_url);
        setUsedTone(data.tone || "honest");
        setOccasion(data.occasion || "Everyday");
        setIsOwner(!!data.is_owner);
        setIsPublic(!!data.is_public);
      })
      .catch(err => { if (!cancelled) setError(err.message || "Couldn't load result"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [paramId, token]);

  const toggleVisibility = async (next) => {
    if (!paramId || !isOwner) return;
    setToggling(true);
    try {
      const res = await fetch(`/api/analyses/${paramId}`, {
        method: "PATCH",
        headers: { ...buildAuthHeaders(token), "Content-Type": "application/json" },
        body: JSON.stringify({ is_public: next }),
      });
      const data = await res.json();
      if (!data.error) setIsPublic(!!data.is_public);
    } finally {
      setToggling(false);
    }
  };

  const handleShare = async () => {
    if (!paramId) return;
    const shareUrl = `${window.location.origin}/result/${paramId}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* ignore */ }
    // Best-effort native share (mobile Safari etc.) — doesn't block on failure
    if (navigator.share) {
      try { await navigator.share({ url: shareUrl, title: "My outfit verdict — Judge the Fit" }); }
      catch { /* user dismissed or unsupported */ }
    }
  };

  if (loading) return (
    <div className="loading" style={{ padding: "80px 0", textAlign: "center" }}>
      <div className="loading-text" style={{ display: "inline-block" }}>Loading</div>
      <div className="dots" style={{ justifyContent: "center", marginTop: 16 }}>
        <div className="dot" /><div className="dot" /><div className="dot" />
      </div>
    </div>
  );
  if (error) return <div className="error" style={{ maxWidth: 640, margin: "80px auto" }}>{error}</div>;
  if (!feedback) return <Navigate to="/" replace />;

  const verdictClass = (v) => v === "WORKS" ? "works" : v === "ALMOST" ? "almost" : "rework";
  const verdictLabel = (v) => v === "WORKS" ? "Works." : v === "ALMOST" ? "Almost." : "Rework.";

  return (
    <div className="result">
      <div className="verdict-row">
        <div className={`verdict-word ${verdictClass(feedback.verdict)}`}>{verdictLabel(feedback.verdict)}</div>
        <div className="score-block">
          <div className="score-num">{feedback.score}</div>
          <div className="score-denom">out of 10</div>
        </div>
      </div>

      <div className="grid">
        <div>
          {image && <img src={image} style={{ width: "100%", aspectRatio: "3/4", objectFit: "cover", display: "block" }} alt="Your outfit" />}
        </div>
        <div>
          <div className="meta-row">
            <span className="tone-badge">{TONES.find(t => t.id === usedTone)?.label} mode</span>
            {isOwner && paramId && (
              <button
                className={`visibility-chip ${isPublic ? "is-public" : "is-private"}`}
                onClick={() => toggleVisibility(!isPublic)}
                disabled={toggling}
                title={isPublic
                  ? "Public — anyone with the link can view. Click to make private."
                  : "Private — only you can view this. Click to make public."}
              >
                {toggling ? "…" : isPublic ? "● Public" : "● Private"}
              </button>
            )}
          </div>
          <p className="summary">{feedback.summary}</p>

          <div className="feedback-grid">
            <div className="feedback-col">
              <span className="section-label">What works</span>
              {feedback.strengths?.map((s, i) => <div key={i} className="feedback-item">{s}</div>)}
            </div>
            <div className="feedback-col">
              <span className="section-label">Fix this</span>
              {feedback.improvements?.map((s, i) => <div key={i} className="feedback-item">{s}</div>)}
            </div>
          </div>

          {feedback.recommendations ? (
            <div className="ideas-section">
              <span className="section-label">Recommendations</span>
              {feedback.recommendations.map((rec, i) => (
                <div key={i} className="rec-item">
                  <div className="rec-header">
                    <span className="rec-num">0{i + 1}</span>
                    <span className="rec-piece">{rec.piece}</span>
                    <span className="rec-tier">{rec.price_tier}</span>
                  </div>
                  <div className="rec-why">{rec.why}</div>
                  {rec.examples && rec.examples.length > 0 && (
                    <div className="rec-examples">
                      {rec.examples.map((ex, j) => {
                        const { label, url } = linkForExample(ex);
                        return (
                          <span key={j}>
                            {j > 0 && "  ·  "}
                            {url
                              ? <a className="example-shop-link" href={url} target="_blank" rel="noopener noreferrer sponsored">{label}</a>
                              : label}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {rec.how_to_wear && <div className="rec-how">{rec.how_to_wear}</div>}
                  <ProductCardRow cards={feedback.products?.by_rec?.[i]} />
                </div>
              ))}
              {feedback.signature_direction && (
                <div className="signature">
                  <span className="section-label">Your signature direction</span>
                  <p className="signature-text">{feedback.signature_direction}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="ideas-section">
              <span className="section-label">Try instead</span>
              {feedback.ideas?.map((idea, i) => (
                <div key={i} className="idea-item">
                  <span className="idea-num">0{i + 1}</span>
                  <span className="idea-text">{idea}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: "40px" }}>
        <button className="share-btn" onClick={handleShare}>
          {copied ? "Link copied ✓" : "Share my verdict"}
        </button>
      </div>

      <div className="reset-row">
        <button className="reset-btn" onClick={resetOptions}>Try different options</button>
        <button className="reset-btn" onClick={resetAll}>Try another look</button>
      </div>

      {feedback.products?.by_rec?.some(arr => arr.length > 0) && (
        <p className="affiliate-disclosure">
          Some product links are affiliate links — we may earn a small commission if you buy something, at no extra cost to you.
        </p>
      )}
    </div>
  );
}

function ProductCardRow({ cards }) {
  if (!Array.isArray(cards) || cards.length === 0) return null;
  return (
    <div className="product-card-row">
      {cards.map((c, i) => (
        <a
          key={i}
          className="product-card"
          href={c.url}
          target="_blank"
          rel="noopener noreferrer sponsored"
        >
          {c.image_url && (
            <div className="product-card-img-wrap">
              <img src={c.image_url} alt={c.title} className="product-card-img" />
            </div>
          )}
          <div className="product-card-body">
            <div className="product-card-brand">{c.brand}</div>
            <div className="product-card-title">{c.title}</div>
            <div className="product-card-meta">
              <span className="product-card-price">{c.price}</span>
              {c.retailer && <span className="product-card-retailer">{c.retailer}</span>}
            </div>
          </div>
        </a>
      ))}
    </div>
  );
}

// ─── App shell (state + routing) ─────────────────────────────────────────────
function AppInner() {
  const [image, setImage]       = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [occasion, setOccasion]  = useState("Everyday");
  const [tone, setTone]          = useState("stylist");
  const [feedback, setFeedback]  = useState(null);
  const [usedTone, setUsedTone]  = useState("stylist");
  const [loading, setLoading]    = useState(false);
  const [error, setError]        = useState(null);
  const [drag, setDrag]          = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const fileRef = useRef();
  const navigate = useNavigate();
  const { user, token, signInWithGoogle, signInWithEmail, signOut } = useAuth();

  const handleFile = useCallback((file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setFeedback(null);
    setError(null);
    setImageFile(file);
    setImage(URL.createObjectURL(file));
  }, []);

  const resetAll = () => {
    setImage(null); setImageFile(null); setFeedback(null); setError(null);
    navigate("/");
  };

  const resetOptions = () => {
    setFeedback(null); setError(null);
    navigate("/");
  };

  const analyze = async (turnstileToken) => {
    if (!imageFile) return;
    setLoading(true); setError(null); setFeedback(null);
    try {
      const formData = new FormData();
      formData.append("image", imageFile);
      formData.append("occasion", occasion);
      formData.append("tone", tone);
      if (turnstileToken) formData.append("cf-turnstile-response", turnstileToken);
      const headers = buildAuthHeaders(token);
      let res;
      try {
        res = await fetch("/api/analyze", {
          method: "POST",
          body: formData,
          headers: Object.keys(headers).length ? headers : undefined,
        });
      } catch (netErr) {
        console.error("[analyze] fetch threw before response:", netErr);
        setError("We couldn't reach the server. Check your connection and try again.");
        return;
      }
      let data = null;
      try { data = await res.json(); }
      catch (parseErr) {
        console.error(`[analyze] response was not JSON (status ${res.status}):`, parseErr);
        if (res.status === 413)      setError("That image is too large — try a smaller photo.");
        else if (res.status === 429) setError("Too many requests right now. Try again in a few minutes.");
        else if (res.status >= 500)  setError("Something went wrong on our end. Please try again.");
        else                         setError("Couldn't read the server response. Please try again.");
        return;
      }
      if (!res.ok || data?.error) {
        setError(data?.error || `Request failed (status ${res.status}).`);
        return;
      }
      setUsedTone(tone);
      setFeedback(data);
      const target = data.id ? `/result/${data.id}` : "/";
      navigate(target, {
        state: {
          feedback: data,
          image,
          usedTone: tone,
          occasion,
          isOwner: !!data.is_owner,
          isPublic: !!data.is_public,
        },
      });
    } catch (err) {
      console.error("[analyze] unexpected error:", err);
      setError("Something unexpected happened. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const generateShareCard = async () => {
    const W = 1080, H = 1920;
    const canvas = document.createElement("canvas");
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d");

    await document.fonts.ready;

    // Full-bleed outfit photo
    const img = new Image();
    img.src = image;
    await new Promise(r => { img.onload = r; img.onerror = r; });
    const scale = Math.max(W / img.width, H / img.height);
    const dw = img.width * scale, dh = img.height * scale;
    ctx.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);

    // Top vignette — masthead readability
    const topGrad = ctx.createLinearGradient(0, 0, 0, 300);
    topGrad.addColorStop(0, "rgba(26,26,24,0.92)");
    topGrad.addColorStop(1, "rgba(26,26,24,0)");
    ctx.fillStyle = topGrad;
    ctx.fillRect(0, 0, W, 300);

    // Bottom vignette — text readability
    const botGrad = ctx.createLinearGradient(0, H * 0.38, 0, H);
    botGrad.addColorStop(0,    "rgba(26,26,24,0)");
    botGrad.addColorStop(0.28, "rgba(26,26,24,0.78)");
    botGrad.addColorStop(0.58, "rgba(26,26,24,0.95)");
    botGrad.addColorStop(1,    "rgba(26,26,24,1)");
    ctx.fillStyle = botGrad;
    ctx.fillRect(0, 0, W, H);

    // Masthead
    ctx.strokeStyle = "rgba(248,247,244,0.55)";
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(72, 72); ctx.lineTo(W - 72, 72); ctx.stroke();
    ctx.fillStyle = "#f8f7f4";
    ctx.font = "300 52px 'Cormorant Garamond', Georgia, serif";
    ctx.letterSpacing = "0.22em";
    ctx.textAlign = "center";
    ctx.fillText("JUDGE THE FIT", W / 2, 138);
    ctx.font = "300 20px 'Montserrat', Arial, sans-serif";
    ctx.letterSpacing = "0.18em";
    ctx.fillStyle = "rgba(248,247,244,0.45)";
    ctx.fillText("YOUR OUTFIT, JUDGED.", W / 2, 170);
    ctx.strokeStyle = "rgba(248,247,244,0.55)";
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(72, 192); ctx.lineTo(W - 72, 192); ctx.stroke();

    // Verdict word (BIG)
    const verdictColor = feedback.verdict === "WORKS"  ? "#f8f7f4"
                       : feedback.verdict === "ALMOST" ? "#c9a96e"
                       : "#e74c3c";
    const verdictLabel = feedback.verdict === "WORKS"  ? "Works."
                       : feedback.verdict === "ALMOST" ? "Almost."
                       : "Rework.";
    ctx.fillStyle = verdictColor;
    ctx.font = "300 190px 'Cormorant Garamond', Georgia, serif";
    ctx.letterSpacing = "-0.02em";
    ctx.textAlign = "left";
    ctx.fillText(verdictLabel, 72, H - 560);

    // Score (right-aligned, same baseline)
    ctx.fillStyle = "#f8f7f4";
    ctx.font = "300 96px 'Cormorant Garamond', Georgia, serif";
    ctx.letterSpacing = "0";
    ctx.textAlign = "right";
    ctx.fillText(feedback.score, W - 72, H - 560);
    ctx.font = "300 26px 'Montserrat', Arial, sans-serif";
    ctx.fillStyle = "rgba(248,247,244,0.4)";
    ctx.fillText("/ 10", W - 72, H - 518);

    // Summary — centered in zone between verdict and rule
    const wrapText = (text, font) => {
      ctx.font = font;
      const words = text.split(" ");
      let line = "", lines = [];
      for (const word of words) {
        const test = line + (line ? " " : "") + word;
        if (ctx.measureText(test).width > W - 160) { lines.push(line); line = word; }
        else line = test;
      }
      lines.push(line);
      return lines;
    };

    const summaryFont = "italic 300 38px 'Cormorant Garamond', Georgia, serif";
    const summaryLines = wrapText(feedback.summary, summaryFont).slice(0, 3);
    const lineH = 56;
    const zoneCenter = ((H - 480) + (H - 240)) / 2;
    const summaryStartY = zoneCenter - (summaryLines.length * lineH) / 2 + lineH * 0.75;
    ctx.fillStyle = "rgba(200,199,192,0.88)";
    ctx.textAlign = "center";
    ctx.letterSpacing = "0";
    summaryLines.forEach((l, i) => { ctx.fillText(l, W / 2, summaryStartY + i * lineH); });

    // Thin rule
    ctx.strokeStyle = "rgba(248,247,244,0.12)";
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(72, H - 220); ctx.lineTo(W - 72, H - 220); ctx.stroke();

    // Occasion · Mode
    ctx.font = "300 20px 'Montserrat', Arial, sans-serif";
    ctx.fillStyle = "rgba(248,247,244,0.38)";
    ctx.textAlign = "center";
    ctx.letterSpacing = "0.18em";
    ctx.fillText(`${occasion.toUpperCase()}  ·  ${(TONES.find(t => t.id === usedTone)?.label || "").toUpperCase()} MODE`, W / 2, H - 170);

    // JUDGETHEFIT.COM
    ctx.fillStyle = "rgba(248,247,244,0.28)";
    ctx.font = "300 22px 'Montserrat', Arial, sans-serif";
    ctx.letterSpacing = "0.28em";
    ctx.textAlign = "center";
    ctx.fillText("JUDGETHEFIT.COM", W / 2, H - 88);

    // Share or download
    canvas.toBlob(async (blob) => {
      const file = new File([blob], "judgethefit-verdict.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My outfit verdict — Judge the Fit" });
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "judgethefit-verdict.png";
        a.click();
      }
    }, "image/png");
  };

  return (
    <>
      <div className="app">
        <header className="masthead">
          <Link to="/" onClick={resetAll} className="masthead-link">
            <span className="masthead-title">Judge the Fit</span>
            <span className="masthead-sub">Your outfit, judged</span>
          </Link>
          <div className="masthead-auth">
            {user ? (
              <>
                <span className="auth-email" title={user.email}>{user.email}</span>
                <button className="auth-chip" onClick={() => signOut()}>Sign out</button>
              </>
            ) : (
              <button className="auth-chip" onClick={() => setShowSignIn(true)}>Sign in</button>
            )}
          </div>
        </header>

        <SignInModal
          open={showSignIn}
          onClose={() => setShowSignIn(false)}
          signInWithGoogle={signInWithGoogle}
          signInWithEmail={signInWithEmail}
        />

        {buildInfo.sha !== "dev" ? (
          <a
            className="version-tag"
            href={`https://github.com/monktonman/judgethefit/commit/${buildInfo.sha}`}
            target="_blank"
            rel="noopener noreferrer"
            title={`built ${buildInfo.buildTime}`}
          >
            v{buildInfo.version} · {buildInfo.sha}
          </a>
        ) : (
          <span className="version-tag" title={`built ${buildInfo.buildTime}`}>
            v{buildInfo.version} · {buildInfo.sha}
          </span>
        )}

        <main className="main">
          <ScrollToTop />
          <Routes>
            <Route path="/" element={
              <HomePage
                image={image} imageFile={imageFile}
                occasion={occasion} setOccasion={setOccasion}
                tone={tone} setTone={setTone}
                loading={loading} error={error}
                drag={drag} setDrag={setDrag}
                handleFile={handleFile} resetAll={resetAll}
                analyze={analyze} fileRef={fileRef}
              />
            } />
            <Route path="/result/:id" element={
              <ResultPage resetAll={resetAll} resetOptions={resetOptions} />
            } />
            <Route path="/result" element={<Navigate to="/" replace />} />
            <Route path="/admin" element={<AdminPage />} />
          </Routes>
        </main>
      </div>
    </>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <style>{STYLES}</style>
      <FlagsProvider>
        <BetaGate>
          <AppInner />
        </BetaGate>
      </FlagsProvider>
    </BrowserRouter>
  );
}
